import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { ColDef } from '@ag-grid-community/core'
import { StatisticalAnalysisDatasourceExternalDetail } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { Nullable } from '@/shared/types/app.types'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDelayAndResponseEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum, StatisticalAnalysisDossiersDatasourceResponsesTypes, StatisticalAnalysisDynamicObject, StatisticalAnalysisExternalListTypes, StatisticalAnalysisViewDetailTableGroupingColumns } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { store } from '@/store/store'
import { StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest, StatisticalAnalysisDelayAndResponseTimesDossierDetailExternalViewTable, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.dto'
import { delayAndResponseTimesDossierService } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.service'
import { chain, isNil } from 'lodash'
import { StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo } from '@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.model'
import { getExternalColumnsEntity, getLabAndCaseId, parseGroupingParams } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'

export class StatisticalAnalysisHandlerDatasourceDelayAndTimeResponsesDossierDetailList extends StatisticalAnalysisDatasourceExternalDetail<StatisticalAnalysisExternalListTypes> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>
    protected row: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>, row: StatisticalAnalysisDossiersDatasourceResponsesTypes) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
        this.row = row as StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed
    }

    public getGridColumns(): ColDef[] {
        return getExternalColumnsEntity((this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters?.typeSearch as StatisticalAnalysisSidebarItemCombo)?.id as StatiscalAnalysisTypeCases)
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisExternalListTypes>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        const baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest = delayAndResponseTimesDossierService.getBaseRequest(sidebarFilter)
        const groupingParams: StatisticalAnalysisDynamicObject = chain(this.row)
            .omitBy((_value: unknown, key: string) => key.startsWith('stats'))
            .omitBy((_value: unknown, key: string) => key.startsWith('timeSeries'))
            .omitBy((value: unknown) => value === null)
            .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
            }, {})
            .value()
        const delayType = sidebarFilter?.typeDelay as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum
        const timeMeasureType = sidebarFilter?.typeDelaysAndResponseTimes?.id as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum
        const entityTypeId = sidebarFilter?.trackingGeneralSelected?.key
        const responseTimeType = sidebarFilter?.trackingDetailSelected?.key as StatisticalAnalysisDelayAndResponseEnum

        const response: StatisticalAnalysisDelayAndResponseTimesDossierDetailExternalViewTable = await delayAndResponseTimesDossierService.getExternalViewDetailList(
            filterModel,
            params,
            typeCase,
            groupingParams,
            entityTypeId,
            responseTimeType,
            delayType,
            timeMeasureType,
            baseRequest,
            scrollId,
            this.getTotalElements()
        )
        if (isNil(this.getTotalElements())) {
            this.setTotalElements(response.scrollPage.totalElements)
        }
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response.scrollPage.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getLabAndCaseId(selectedRow: StatisticalAnalysisExternalListTypes): [number, number] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        return getLabAndCaseId(typeCase, selectedRow)
    }
}
